import * as Sentry from '@sentry/react'

import { HttpError } from '@/core/api/errors/HttpError'
import { sessionService } from '@/core/auth/session/session.service'

interface BffErrorBody {
  success: false
  data: unknown | null
  error?: {
    code?: string | null
    message?: string | null
    details?: unknown | null
  } | null
}

export async function errorResponseHook(
  request: Request,
  response: Response,
): Promise<Response> {
  if (response.ok) return response

  const body = await response.clone().json().catch(() => null) as Partial<BffErrorBody> | null
  const errorCode = body?.error?.code ?? null

  if (response.status === 401) {
    // Endpoints públicos que usan 401 con significado propio (credenciales/código
    // incorrecto) y NO deben tratarse como sesión expirada.
    const pathname = new URL(request.url).pathname
    const isPublicEndpoint =
      pathname === '/api/auth/login' ||
      pathname.startsWith('/api/registration/') ||
      pathname.startsWith('/api/biometria/') ||
      pathname.startsWith('/api/password-reset/')

    // Un endpoint protegido que responde 401 significa que la sesión ya no
    // es válida. No dependemos de `error.code`, porque algunos BFF pueden
    // responder 401 sin propagar un código de negocio.
    if (!isPublicEndpoint) {
      await sessionService.handleUnauthorized()
      // AuthGuard observa el estado de sesión y redirige a /login. Evitamos
      // que los hooks de la pantalla muestren errores mientras se desmontan.
      return new Promise<Response>(() => {})
    }
  }

  const error = new HttpError(response, body)

  // Capture with business context so Sentry can filter by error code.
  Sentry.withScope((scope) => {
    scope.setTag('http.status', response.status)
    if (errorCode) scope.setTag('error_code', errorCode)
    scope.setContext('api_error', {
      url: request.url,
      method: request.method,
      status: response.status,
      errorCode,
      errorMessage: body?.error?.message ?? null,
    })
    Sentry.captureException(error)
  })

  throw error
}
